import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import PortalLayout from "../components/PortalLayout";
import AdminSubNav from "../components/AdminSubNav";
import { useAdminAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, X, Pencil, Sparkles, Loader2, Mail, Wand2, Trophy } from "lucide-react";

/**
 * Admin moderation queue for member-submitted angel attributes.
 * Tabs across pending / approved / rejected. Approving lets the curator
 * lightly edit the wording (max 60 chars) before it goes live.
 */
function formatDate(iso) {
    try {
        return new Date(iso).toLocaleString(undefined, {
            year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

export default function AdminAttributes() {
    const { admin } = useAdminAuth();
    const location = useLocation();
    const [statusFilter, setStatusFilter] = useState("pending");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState({}); // { [id]: editedText }
    const [busy, setBusy] = useState({}); // { [id]: true }
    const [sendingDigest, setSendingDigest] = useState(false);
    const [highlightId, setHighlightId] = useState(null);
    const rowRefs = useRef({});
    const [autoPreview, setAutoPreview] = useState(null); // { safe[], skipped[], rules } | null
    const [autoLoading, setAutoLoading] = useState(false);
    const [autoExecuting, setAutoExecuting] = useState(false);
    const [wordsmithPreview, setWordsmithPreview] = useState(null);
    const [wordsmithLoading, setWordsmithLoading] = useState(false);
    const [wordsmithSending, setWordsmithSending] = useState(false);

    const load = async (filter = statusFilter) => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/attributes?status_filter=${filter}`);
            setItems(data.items || []);
            setEditing({});
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load(statusFilter);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    // Deep-link support: `/admin/attributes#attr-<id>` (from the weekly digest
    // email). When the page lands with such a hash, scroll the row into view
    // and pulse it in brass so the curator's eye lands on it immediately.
    useEffect(() => {
        if (loading) return;
        const hash = location.hash || window.location.hash || "";
        const m = hash.match(/^#attr-(.+)$/);
        if (!m) return;
        const targetId = m[1];
        const found = items.find((it) => it.id === targetId);
        if (!found) return;
        // If the deep-linked row lives in a different tab, switch to it.
        if (found.status && found.status !== statusFilter) {
            setStatusFilter(found.status);
            return;
        }
        setHighlightId(targetId);
        const node = rowRefs.current[targetId];
        if (node) {
            try { node.scrollIntoView({ behavior: "smooth", block: "center" }); }
            catch { /* ignore */ }
        }
        const t = window.setTimeout(() => setHighlightId(null), 4500);
        return () => window.clearTimeout(t);
    }, [loading, items, location.hash, statusFilter]);

    const sendDigestNow = async () => {
        setSendingDigest(true);
        try {
            const { data } = await api.post("/admin/attributes/digest/send-now");
            if (!data.sent) {
                if (data.reason === "no_pending") {
                    toast("No pending suggestions to digest.", {
                        description: "Quiet week — no email sent.",
                    });
                } else {
                    toast.error("Digest could not be sent. Check email logs.");
                }
            } else {
                toast.success(`Digest sent to ${data.recipient}`, {
                    description: `${data.count} pending suggestion${data.count === 1 ? "" : "s"} included.`,
                });
            }
        } catch {
            toast.error("Could not send digest. Try again.");
        } finally {
            setSendingDigest(false);
        }
    };

    const startEdit = (item) => {
        setEditing((prev) => ({ ...prev, [item.id]: item.text }));
    };
    const cancelEdit = (id) => {
        setEditing((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const approve = async (item) => {
        setBusy((b) => ({ ...b, [item.id]: true }));
        try {
            const editedText = editing[item.id];
            const payload = editedText && editedText.trim() && editedText.trim() !== item.text
                ? { text: editedText.trim().slice(0, 60) }
                : {};
            await api.post(`/admin/attributes/${item.id}/approve`, payload);
            toast.success("Attribute approved.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not approve.");
        } finally {
            setBusy((b) => ({ ...b, [item.id]: false }));
        }
    };

    const reject = async (item) => {
        setBusy((b) => ({ ...b, [item.id]: true }));
        try {
            await api.post(`/admin/attributes/${item.id}/reject`);
            toast.success("Attribute rejected.");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not reject.");
        } finally {
            setBusy((b) => ({ ...b, [item.id]: false }));
        }
    };

    /** Dry-run the auto-approve classifier and open the preview modal. */
    const openAutoApprovePreview = async () => {
        setAutoLoading(true);
        try {
            const { data } = await api.post("/admin/attributes/auto-approve-preview");
            setAutoPreview(data);
        } catch {
            toast.error("Could not preview auto-approve.");
        } finally {
            setAutoLoading(false);
        }
    };

    /** Execute the sweep — approves every safe row, leaves flagged rows for human review. */
    const runAutoApprove = async () => {
        setAutoExecuting(true);
        try {
            const { data } = await api.post("/admin/attributes/auto-approve");
            const { approvedCount, skippedCount } = data;
            if (approvedCount === 0) {
                toast("Nothing safe to auto-approve.", {
                    description: `${skippedCount} suggestion${skippedCount === 1 ? "" : "s"} need your eye.`,
                });
            } else {
                toast.success(`Auto-approved ${approvedCount} suggestion${approvedCount === 1 ? "" : "s"}.`, {
                    description: skippedCount > 0
                        ? `${skippedCount} flagged for your review.`
                        : "Queue is clear.",
                });
            }
            setAutoPreview(null);
            await load();
        } catch {
            toast.error("Auto-approve failed. Try again.");
        } finally {
            setAutoExecuting(false);
        }
    };

    /** Preview the year-end wordsmith leaderboard (no email sent). */
    const openWordsmithPreview = async () => {
        setWordsmithLoading(true);
        try {
            const { data } = await api.post("/admin/wordsmiths/preview");
            setWordsmithPreview(data);
        } catch {
            toast.error("Could not load the wordsmith leaderboard.");
        } finally {
            setWordsmithLoading(false);
        }
    };

    /** Send the curator + per-contributor wordsmith emails now (test/early run). */
    const sendWordsmithDigestNow = async () => {
        const confirm = window.confirm(
            "Send the year-end wordsmith emails now? This will mail the curator AND every contributor (unless they have wordsmithDigestOptOut set)."
        );
        if (!confirm) return;
        setWordsmithSending(true);
        try {
            const { data } = await api.post("/admin/wordsmiths/send-now");
            toast.success("Wordsmith digest dispatched.", {
                description: `Curator: ${data.curatorSent ? "sent" : "failed"} · members: ${data.memberSent} sent, ${data.memberSkipped} skipped, ${data.memberFailed} failed`,
            });
            setWordsmithPreview(null);
        } catch {
            toast.error("Wordsmith dispatch failed. Check email logs.");
        } finally {
            setWordsmithSending(false);
        }
    };

    const counts = useMemo(() => ({ pending: items.length }), [items]);

    if (admin === null) return null;
    if (!admin) return <Navigate to="/login" replace />;

    const tabs = [
        { key: "pending", label: "Pending" },
        { key: "approved", label: "Approved" },
        { key: "rejected", label: "Rejected" },
    ];

    return (
        <PortalLayout>
            <section className="max-w-6xl mx-auto px-6 md:px-12 lg:px-16 pt-16 pb-32" data-testid="admin-attributes-page">
                <div className="mb-10">
                    <span className="font-label text-[11px] text-[#cfa052]">Admin · attributes</span>
                    <div className="mt-4 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                        <div>
                            <h1 className="font-serif-display text-5xl md:text-6xl tracking-tighter text-[#e8e4db]" data-testid="admin-attributes-title">
                                Words for the <span className="italic">angels</span>
                            </h1>
                            <p className="mt-4 font-poetic text-[#9a958a] max-w-2xl">
                                Members suggest the qualities they sense in each angel. Approve, tweak, or release. A weekly digest of pending suggestions lands in your inbox every Sunday morning.
                            </p>
                        </div>
                        <div className="self-start md:self-auto flex flex-col sm:flex-row gap-3">
                            <button
                                type="button"
                                onClick={openWordsmithPreview}
                                disabled={wordsmithLoading}
                                data-testid="admin-attributes-wordsmith-btn"
                                className="inline-flex items-center gap-2 border border-white/10 hover:border-[#cfa052]/40 text-[#9a958a] hover:text-[#cfa052] px-5 py-2.5 font-label text-[10px] tracking-[0.25em] uppercase rounded-sm transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                                title="Preview the year-end voice leaderboard"
                            >
                                {wordsmithLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" />}
                                Wordsmiths {new Date().getFullYear()}
                            </button>
                            <button
                                type="button"
                                onClick={sendDigestNow}
                                disabled={sendingDigest}
                                data-testid="admin-attributes-send-digest-btn"
                                className="inline-flex items-center gap-2 border border-[#cfa052]/40 hover:border-[#cfa052] text-[#cfa052] hover:text-[#e0b569] px-5 py-2.5 font-label text-[10px] tracking-[0.25em] uppercase rounded-sm transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {sendingDigest ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                                Send digest now
                            </button>
                        </div>
                    </div>
                </div>

                <AdminSubNav />

                {/* Status tabs */}
                <div className="flex items-center justify-between gap-4 mb-8 flex-wrap" data-testid="admin-attributes-tabs">
                    <div className="flex items-center gap-2">
                    {tabs.map((t) => (
                        <button
                            key={t.key}
                            type="button"
                            onClick={() => setStatusFilter(t.key)}
                            data-testid={`admin-attributes-tab-${t.key}`}
                            className={`font-label text-[10px] tracking-[0.25em] uppercase px-4 py-2 rounded-full border transition-colors ${
                                statusFilter === t.key
                                    ? "border-[#cfa052] text-[#cfa052] bg-[#cfa052]/[0.08]"
                                    : "border-white/10 text-[#9a958a] hover:text-[#e8e4db]"
                            }`}
                        >
                            {t.label}
                            {statusFilter === t.key && t.key === "pending" && counts.pending > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-[#cfa052] text-[#0c0b0a] px-1 font-numerals text-[10px]">
                                    {counts.pending}
                                </span>
                            )}
                        </button>
                    ))}
                    </div>
                    {statusFilter === "pending" && items.length > 0 && (
                        <button
                            type="button"
                            onClick={openAutoApprovePreview}
                            disabled={autoLoading}
                            data-testid="admin-attributes-auto-approve-btn"
                            className="inline-flex items-center gap-2 border border-[#cfa052]/40 hover:border-[#cfa052] text-[#cfa052] hover:text-[#e0b569] px-4 py-2 font-label text-[10px] tracking-[0.25em] uppercase rounded-full transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                            {autoLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                            Auto-approve safe
                        </button>
                    )}
                </div>

                {loading ? (
                    <div className="text-center py-32 font-poetic text-[#9a958a]" data-testid="admin-attributes-loading">
                        weighing the words…
                    </div>
                ) : items.length === 0 ? (
                    <div className="text-center py-32" data-testid="admin-attributes-empty">
                        <Sparkles className="w-8 h-8 mx-auto text-[#6e6a62] mb-3" />
                        <p className="font-poetic text-[#9a958a]">
                            {statusFilter === "pending"
                                ? "No suggestions waiting."
                                : statusFilter === "approved"
                                    ? "No approved attributes yet."
                                    : "Nothing rejected."}
                        </p>
                    </div>
                ) : (
                    <ul className="divide-y divide-white/5" data-testid="admin-attributes-list">
                        {items.map((it) => {
                            const isEditing = editing[it.id] !== undefined;
                            const isBusy = busy[it.id];
                            const isHighlighted = highlightId === it.id;
                            return (
                                <li
                                    key={it.id}
                                    id={`attr-${it.id}`}
                                    ref={(el) => { rowRefs.current[it.id] = el; }}
                                    className={`py-6 grid md:grid-cols-[100px,1fr,auto] gap-4 md:gap-8 items-start transition-colors duration-700 ${isHighlighted ? "bg-[#cfa052]/[0.08] -mx-3 px-3 rounded" : ""}`}
                                    data-testid={`admin-attribute-row-${it.id}`}
                                >
                                    <div>
                                        <div className="font-numerals text-3xl text-[#cfa052] leading-none">
                                            {it.angelNumber}
                                        </div>
                                        <div className="mt-1 font-label text-[10px] tracking-[0.2em] uppercase text-[#6e6a62]">
                                            Angel
                                        </div>
                                    </div>
                                    <div className="min-w-0">
                                        {isEditing ? (
                                            <input
                                                type="text"
                                                value={editing[it.id]}
                                                maxLength={60}
                                                onChange={(e) =>
                                                    setEditing((prev) => ({ ...prev, [it.id]: e.target.value }))
                                                }
                                                data-testid={`admin-attribute-edit-input-${it.id}`}
                                                className="w-full bg-[#151311] border border-[#cfa052]/40 focus:border-[#cfa052] outline-none rounded-sm px-3 py-2 font-poetic text-base text-[#e8e4db]"
                                            />
                                        ) : (
                                            <div
                                                className="font-poetic text-lg text-[#e8e4db]"
                                                data-testid={`admin-attribute-text-${it.id}`}
                                            >
                                                {it.text}
                                            </div>
                                        )}
                                        <div className="mt-2 font-label text-[10px] text-[#6e6a62] tracking-wider">
                                            From <span className="text-[#9a958a]">{it.submittedByName || "—"}</span>
                                            <span className="px-2 text-[#3a3631]">·</span>
                                            {formatDate(it.submittedAt)}
                                            {it.reviewedAt && (
                                                <>
                                                    <span className="px-2 text-[#3a3631]">·</span>
                                                    reviewed {formatDate(it.reviewedAt)}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {statusFilter === "pending" && !isEditing && (
                                            <button
                                                type="button"
                                                onClick={() => startEdit(it)}
                                                disabled={isBusy}
                                                data-testid={`admin-attribute-edit-btn-${it.id}`}
                                                className="inline-flex items-center gap-1.5 border border-white/10 hover:border-white/30 text-[#9a958a] hover:text-[#e8e4db] px-3 py-2 font-label text-[10px] tracking-[0.2em] uppercase rounded-sm transition-colors disabled:opacity-50"
                                            >
                                                <Pencil className="w-3 h-3" />
                                                Edit
                                            </button>
                                        )}
                                        {statusFilter === "pending" && isEditing && (
                                            <button
                                                type="button"
                                                onClick={() => cancelEdit(it.id)}
                                                disabled={isBusy}
                                                data-testid={`admin-attribute-edit-cancel-${it.id}`}
                                                className="inline-flex items-center gap-1.5 border border-white/10 hover:border-white/30 text-[#9a958a] hover:text-[#e8e4db] px-3 py-2 font-label text-[10px] tracking-[0.2em] uppercase rounded-sm transition-colors disabled:opacity-50"
                                            >
                                                <X className="w-3 h-3" />
                                                Undo edit
                                            </button>
                                        )}
                                        {statusFilter === "pending" && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={() => approve(it)}
                                                    disabled={isBusy}
                                                    data-testid={`admin-attribute-approve-${it.id}`}
                                                    className="inline-flex items-center gap-1.5 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-4 py-2 font-label text-[10px] tracking-[0.2em] uppercase rounded-sm transition-colors disabled:opacity-50"
                                                >
                                                    {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                                    Approve
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => reject(it)}
                                                    disabled={isBusy}
                                                    data-testid={`admin-attribute-reject-${it.id}`}
                                                    className="inline-flex items-center gap-1.5 border border-white/20 hover:border-[#d97c6a] text-[#9a958a] hover:text-[#d97c6a] px-4 py-2 font-label text-[10px] tracking-[0.2em] uppercase rounded-sm transition-colors disabled:opacity-50"
                                                >
                                                    <X className="w-3 h-3" />
                                                    Reject
                                                </button>
                                            </>
                                        )}
                                        {statusFilter === "approved" && (
                                            <span className="font-label text-[10px] tracking-[0.2em] uppercase text-[#cfa052] inline-flex items-center gap-1.5">
                                                <Check className="w-3 h-3" /> live
                                                <span className="ml-2 text-[#6e6a62]">
                                                    {(it.upvotes?.length || 0)} upvote{(it.upvotes?.length || 0) === 1 ? "" : "s"}
                                                </span>
                                            </span>
                                        )}
                                        {statusFilter === "rejected" && (
                                            <span className="font-label text-[10px] tracking-[0.2em] uppercase text-[#6e6a62]">
                                                rejected
                                            </span>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </section>
            {autoPreview && (
                <div
                    className="fixed inset-0 z-[100] bg-[#0c0b0a]/85 backdrop-blur-sm flex items-center justify-center px-6 py-10"
                    role="dialog"
                    aria-modal="true"
                    data-testid="auto-approve-modal"
                    onClick={(e) => { if (e.target === e.currentTarget) setAutoPreview(null); }}
                >
                    <div className="max-w-2xl w-full bg-[#151311] border border-[#cfa052]/30 rounded-sm shadow-2xl flex flex-col max-h-[80vh]">
                        <div className="p-7 border-b border-white/5">
                            <span className="font-label text-[10px] tracking-[0.3em] uppercase text-[#cfa052]">Auto-approve · preview</span>
                            <h2 className="mt-3 font-serif-display text-3xl text-[#e8e4db] tracking-tight">
                                <span className="text-[#cfa052]">{autoPreview.safeCount}</span> safe · <span className="text-[#9a958a]">{autoPreview.skippedCount}</span> need a human eye
                            </h2>
                            <p className="mt-3 font-poetic text-sm text-[#9a958a]">
                                The safe ones are short, calm, and free of URLs, profanity, or shouting. Flagged ones stay in your queue for you to read.
                            </p>
                        </div>
                        <div className="flex-1 overflow-y-auto p-7 space-y-6">
                            {autoPreview.safeCount > 0 && (
                                <div data-testid="auto-approve-safe-list">
                                    <h3 className="font-label text-[10px] tracking-[0.25em] uppercase text-[#cfa052] mb-3">Will approve ({autoPreview.safeCount})</h3>
                                    <ul className="space-y-2">
                                        {autoPreview.safe.map((it) => (
                                            <li key={it.id} className="flex items-center gap-3 font-poetic text-sm text-[#e8e4db]" data-testid={`auto-approve-safe-${it.id}`}>
                                                <span className="font-numerals text-[#cfa052] text-base w-10">{it.angelNumber}</span>
                                                <span className="flex-1">{it.text}</span>
                                                <span className="text-[#6e6a62] text-xs">{it.submittedByName}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {autoPreview.skippedCount > 0 && (
                                <div data-testid="auto-approve-skipped-list">
                                    <h3 className="font-label text-[10px] tracking-[0.25em] uppercase text-[#9a958a] mb-3">Will keep for review ({autoPreview.skippedCount})</h3>
                                    <ul className="space-y-2">
                                        {autoPreview.skipped.map((it) => (
                                            <li key={it.id} className="flex items-center gap-3 font-poetic text-sm text-[#9a958a]" data-testid={`auto-approve-skipped-${it.id}`}>
                                                <span className="font-numerals text-[#6e6a62] text-base w-10">{it.angelNumber}</span>
                                                <span className="flex-1">{it.text}</span>
                                                <span className="font-label text-[9px] tracking-[0.2em] uppercase text-[#d97c6a] whitespace-nowrap">
                                                    {it.skipReason.replace(/_/g, " ")}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {autoPreview.safeCount === 0 && autoPreview.skippedCount === 0 && (
                                <p className="font-poetic text-center text-[#9a958a]">Queue is empty.</p>
                            )}
                        </div>
                        <div className="p-7 border-t border-white/5 flex flex-col sm:flex-row gap-3 justify-end">
                            <button
                                type="button"
                                onClick={() => setAutoPreview(null)}
                                disabled={autoExecuting}
                                data-testid="auto-approve-cancel"
                                className="border border-white/20 hover:border-[#e8e4db]/60 text-[#e8e4db] px-6 py-3 font-label text-[10px] tracking-[0.25em] uppercase rounded-sm transition-colors disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={runAutoApprove}
                                disabled={autoExecuting || autoPreview.safeCount === 0}
                                data-testid="auto-approve-confirm"
                                className="inline-flex items-center gap-2 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-6 py-3 font-label text-[10px] tracking-[0.25em] uppercase rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {autoExecuting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />}
                                Approve {autoPreview.safeCount} now
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {wordsmithPreview && (
                <div
                    className="fixed inset-0 z-[100] bg-[#0c0b0a]/85 backdrop-blur-sm flex items-center justify-center px-6 py-10"
                    role="dialog"
                    aria-modal="true"
                    data-testid="wordsmith-modal"
                    onClick={(e) => { if (e.target === e.currentTarget) setWordsmithPreview(null); }}
                >
                    <div className="max-w-2xl w-full bg-[#151311] border border-[#cfa052]/30 rounded-sm shadow-2xl flex flex-col max-h-[85vh]">
                        <div className="p-7 border-b border-white/5">
                            <span className="font-label text-[10px] tracking-[0.3em] uppercase text-[#cfa052]">Wordsmiths · {wordsmithPreview.year}</span>
                            <h2 className="mt-3 font-serif-display text-3xl text-[#e8e4db] tracking-tight">
                                The voices that shaped <span className="italic text-[#cfa052]">{wordsmithPreview.year}</span>
                            </h2>
                            <p className="mt-3 font-poetic text-sm text-[#9a958a]">
                                <span className="text-[#e8e4db]">{wordsmithPreview.contributorCount}</span> contributor{wordsmithPreview.contributorCount === 1 ? "" : "s"} · <span className="text-[#e8e4db]">{wordsmithPreview.totalAttributes}</span> approved attribute{wordsmithPreview.totalAttributes === 1 ? "" : "s"} · <span className="text-[#e8e4db]">{wordsmithPreview.totalUpvotes}</span> upvote{wordsmithPreview.totalUpvotes === 1 ? "" : "s"} earned.
                            </p>
                            <p className="mt-2 font-label text-[10px] tracking-[0.2em] uppercase text-[#6e6a62]">
                                Auto-sends {wordsmithPreview.scheduledFor} · curator: {wordsmithPreview.curatorRecipient}
                            </p>
                        </div>
                        <div className="flex-1 overflow-y-auto p-7" data-testid="wordsmith-list">
                            {wordsmithPreview.contributorCount === 0 ? (
                                <p className="font-poetic text-center text-[#9a958a]">
                                    No approved attributes in {wordsmithPreview.year} yet.
                                </p>
                            ) : (
                                <ol className="space-y-3">
                                    {wordsmithPreview.leaderboard.slice(0, 10).map((entry, i) => (
                                        <li
                                            key={entry.memberId}
                                            className="flex items-center gap-4"
                                            data-testid={`wordsmith-row-${entry.memberId}`}
                                        >
                                            <span className="font-numerals text-2xl text-[#6e6a62] w-8 tabular-nums">
                                                {String(i + 1).padStart(2, "0")}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-poetic text-[#e8e4db] truncate">{entry.name}</div>
                                                <div className="font-label text-[10px] tracking-[0.2em] uppercase text-[#6e6a62] truncate">
                                                    {entry.email || <span className="text-[#d97c6a]">no email on file</span>}
                                                    {entry.optOut && <span className="ml-2 text-[#d97c6a]">· opted out</span>}
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-numerals text-xl text-[#cfa052]">{entry.count}</div>
                                                <div className="font-label text-[9px] tracking-[0.2em] uppercase text-[#6e6a62]">attrs</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-numerals text-xl text-[#9a958a]">{entry.upvotes}</div>
                                                <div className="font-label text-[9px] tracking-[0.2em] uppercase text-[#6e6a62]">upvotes</div>
                                            </div>
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </div>
                        <div className="p-7 border-t border-white/5 flex flex-col sm:flex-row gap-3 justify-end">
                            <button
                                type="button"
                                onClick={() => setWordsmithPreview(null)}
                                disabled={wordsmithSending}
                                data-testid="wordsmith-close"
                                className="border border-white/20 hover:border-[#e8e4db]/60 text-[#e8e4db] px-6 py-3 font-label text-[10px] tracking-[0.25em] uppercase rounded-sm transition-colors disabled:opacity-50"
                            >
                                Close
                            </button>
                            <button
                                type="button"
                                onClick={sendWordsmithDigestNow}
                                disabled={wordsmithSending || wordsmithPreview.contributorCount === 0}
                                data-testid="wordsmith-send-now"
                                className="inline-flex items-center gap-2 bg-[#cfa052] hover:bg-[#e0b569] text-[#0c0b0a] px-6 py-3 font-label text-[10px] tracking-[0.25em] uppercase rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {wordsmithSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                                Send year-end emails now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </PortalLayout>
    );
}
